import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { createMutationHook } from './createMutation';
import { buildQueryString } from '../../utils/buildQueryString';

// --- Types ---

export interface IXCrawledPost {
  id: string;
  xPostId: string;
  channelId: string | null;
  authorUsername: string;
  originalText: string;
  translatedText: string | null;
  topics: string[] | null;
  holdReason: 'sensitive' | 'error' | 'unposted' | null;
  language: string | null;
  mediaUrls: string[] | null;
  engagement: {
    likes: number;
    retweets: number;
    replies: number;
    views: number;
  };
  publishedAt: string;
  collectedAt: string;
  channel?: {
    id: string;
    username: string;
    displayName: string | null;
  };
}

export interface IXChannel {
  id: string;
  username: string;
  displayName: string | null;
  isActive: boolean;
  postCount: number;
}

interface IListResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// --- Query Key Factory ---

export const xPostsKeys = {
  all: ['x-posts'] as const,
  lists: () => [...xPostsKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) =>
    [...xPostsKeys.lists(), params] as const,
  topics: () => [...xPostsKeys.all, 'topics'] as const,
  channels: () => ['x-channels'] as const,
};

// --- Query Hooks ---

export interface IXPostsListParams {
  page: number;
  limit: number;
  search: string;
  topic: string;
  status?: string;
  holdReason?: string;
}

export const useXPostsList = (params: IXPostsListParams) =>
  useQuery({
    queryKey: xPostsKeys.list(params as unknown as Record<string, unknown>),
    queryFn: () =>
      api.get<IListResponse<IXCrawledPost>>(
        `/admin/x-posts?${buildQueryString({
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          topic:
            params.topic && params.topic !== 'all' ? params.topic : undefined,
          status: params.status || undefined,
          holdReason: params.holdReason || undefined,
        })}`,
      ),
    placeholderData: (previousData) => previousData,
  });

export const useXTopics = () =>
  useQuery({
    queryKey: xPostsKeys.topics(),
    queryFn: () => api.get<{ data: string[] }>('/admin/x-posts/topics'),
    staleTime: 1000 * 60 * 30, // 30 min — topics rarely change
    select: (res) => res.data,
  });

export const useXChannels = () =>
  useQuery({
    queryKey: xPostsKeys.channels(),
    queryFn: () =>
      api.get<IListResponse<IXChannel>>('/admin/x-channels?limit=100'),
    select: (res) => res.data,
  });

export const useCrawlStatus = () =>
  useQuery({
    queryKey: [...xPostsKeys.all, 'crawl-status'] as const,
    queryFn: () =>
      api.get<{ isCrawling: boolean }>('/admin/x-posts/crawl/status'),
    refetchInterval: (query) => (query.state.data?.isCrawling ? 3000 : false),
  });

// --- Mutation Hooks ---

export const useDeleteXPost = createMutationHook<unknown, string>({
  mutationFn: (id) => api.delete(`/admin/x-posts/${id}`),
  invalidateKeys: [xPostsKeys.lists()],
  successMessage: 'x_posts.toast.post_deleted',
  errorMessage: 'x_posts.toast.delete_failed',
});

export const useTogglePublishXPost = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      id,
      holdReason,
    }: {
      id: string;
      holdReason: string | null;
    }) => {
      const action = holdReason === null ? 'unpublish' : 'publish';
      return api.post(`/admin/x-posts/${id}/${action}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: xPostsKeys.lists() });
      toast.success(
        variables.holdReason === null
          ? t('x_posts.toast.unpublished')
          : t('x_posts.toast.published'),
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || t('x_posts.toast.toggle_failed'));
    },
  });
};

export const useUpdateXPost = createMutationHook<
  unknown,
  {
    id: string;
    data: {
      originalText?: string;
      translatedText?: string | null;
      topics?: string[];
      mediaUrls?: string[] | null;
    };
  }
>({
  mutationFn: ({ id, data }) => api.patch(`/admin/x-posts/${id}`, data),
  invalidateKeys: [xPostsKeys.lists()],
  successMessage: 'x_posts.toast.post_updated',
  errorMessage: 'x_posts.toast.update_failed',
});

export const useCreateXPost = createMutationHook<
  unknown,
  {
    originalText: string;
    translatedText?: string;
    topics?: string[];
    mediaUrls?: string[];
  }
>({
  mutationFn: (data) => api.post('/admin/x-posts', data),
  invalidateKeys: [xPostsKeys.lists()],
  successMessage: 'x_posts.toast.post_created',
  errorMessage: 'x_posts.toast.create_failed',
});

export const useClassifyXPost = createMutationHook<{ jobId: string }, string>({
  mutationFn: (id) =>
    api.post<{ jobId: string }>(`/admin/x-posts/${id}/classify`),
  invalidateKeys: [],
  successMessage: 'x_posts.toast.classify_queued',
  errorMessage: 'x_posts.toast.classify_failed',
});

export const useGenerateImage = createMutationHook<{ jobId: string }, string>({
  mutationFn: (id) =>
    api.post<{ jobId: string }>(`/admin/x-posts/${id}/generate-image`),
  invalidateKeys: [],
  successMessage: 'x_posts.toast.generate_image_queued',
  errorMessage: 'x_posts.toast.generate_image_failed',
});

export const useTranslateImages = createMutationHook<{ jobId: string }, string>(
  {
    mutationFn: (id) =>
      api.post<{ jobId: string }>(`/admin/x-posts/${id}/translate-images`),
    invalidateKeys: [],
    successMessage: 'x_posts.toast.translate_images_queued',
    errorMessage: 'x_posts.toast.translate_images_failed',
  },
);

export const useGenerateThumbnails = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () =>
      api.post<{ processed: number; failed: number }>(
        '/admin/x-posts/generate-thumbnails',
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: xPostsKeys.lists() });
      toast.success(
        t('x_posts.toast.generate_thumbnails_success', {
          processed: data.processed,
          failed: data.failed,
        }),
      );
    },
    onError: (error: Error) => {
      toast.error(
        error.message || t('x_posts.toast.generate_thumbnails_failed'),
      );
    },
  });
};

export const useSyncFollowings = createMutationHook<unknown, void>({
  mutationFn: () => api.post('/admin/x-posts/sync-followings'),
  invalidateKeys: [xPostsKeys.channels()],
  successMessage: 'x_posts.toast.sync_followings_success',
  errorMessage: 'x_posts.toast.sync_followings_failed',
});

export const useTriggerCrawl = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (channelId?: string) => {
      const path = channelId
        ? `/admin/x-posts/crawl/${channelId}`
        : '/admin/x-posts/crawl';
      return api.post(path);
    },
    onSuccess: (_data, channelId) => {
      toast.success(
        channelId
          ? t('x_posts.toast.channel_crawl_queued')
          : t('x_posts.toast.crawl_queued'),
      );

      queryClient.setQueryData([...xPostsKeys.all, 'crawl-status'], {
        isCrawling: true,
      });

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: xPostsKeys.lists() });
      }, 5000);
    },
    onError: (error: Error) => {
      toast.error(error.message || t('x_posts.toast.crawl_failed'));
    },
  });
};

export const useCancelAllJobs = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => api.post('/admin/x-posts/cancel-all-jobs'),
    onSuccess: () => {
      queryClient.setQueryData([...xPostsKeys.all, 'crawl-status'], {
        isCrawling: false,
      });
      toast.success(t('x_posts.toast.jobs_cancelled'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('x_posts.toast.cancel_failed'));
    },
  });
};

export const useAddXChannel = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (username: string) =>
      api.post('/admin/x-channels', { username }),
    onSuccess: (_data, username) => {
      queryClient.invalidateQueries({ queryKey: xPostsKeys.channels() });
      toast.success(t('x_posts.toast.channel_added', { username }));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('x_posts.toast.channel_add_failed'));
    },
  });
};

export const useRemoveXChannel = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id }: { id: string; username: string }) =>
      api.delete(`/admin/x-channels/${id}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: xPostsKeys.channels() });
      toast.success(
        t('x_posts.toast.channel_removed', { username: variables.username }),
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || t('x_posts.toast.channel_remove_failed'));
    },
  });
};
